import React, { useEffect, useRef, useState } from 'react';
import { formatMoney } from '@belong/common';
import { Text, LinkBase } from '@belong/ui';
import cx from 'clsx';
import { ListingApplicationListingApplicationModel } from 'api/models';
import BulletList from 'components/BulletList/BulletList';
import UnitBasicInfo from 'models/common/UnitBasicInfo';
import { ApplicationStatus, ListingApplicationStatus } from 'models/enums';
import { BASE_PATHS } from 'routes/paths';
import HomeInfo from '../../components/display/Home/HomeInfo/HomeInfo';
import styles from './TourAndAppsCard.module.css';

type Props = {
  onWithdraw?: () => void;
  className?: string;
};

function HoverWithdraw({ onWithdraw, className = '' }: Props) {
  const [visible, setVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setVisible(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`group absolute right-xs top-xs z-50 inline-flex flex-col items-end md:right-0 md:top-0 ${className}`}
    >
      <button
        type="button"
        aria-label="More actions"
        onClick={() => setVisible((prev) => !prev)}
        className={cx(
          'px-xs py-xs rounded-[4px] focus:outline-none',
          'md:bg-transparent md:hover:bg-light-gray',
          'transition-bg duration-hover',
          'bg-dark-navy-translucent opacity-50 md:bg-transparent md:opacity-100',
        )}
      >
        <span className="flex h-sm w-sm flex-col items-center justify-center gap-2xs">
          <span className="h-2xs w-2xs rounded-full bg-white md:bg-navy" />
          <span className="h-2xs w-2xs rounded-full bg-white md:bg-navy" />
          <span className="h-2xs w-2xs rounded-full bg-white md:bg-navy" />
        </span>
      </button>

      <button
        type="button"
        onClick={onWithdraw}
        className={`
          ${visible ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}
          select-none rounded-small
          border
          border-solid
          border-gray
          bg-white
          px-sm
          py-xs font-semibold tracking-lg
          text-navy
          transition-opacity duration-hover
        `}
      >
        WITHDRAW
      </button>
    </div>
  );
}

export const AppCardTitleWithDetails = ({
  title,
  basicInfo,
  rentAmount,
  homeUniqueId,
  isGrayscale,
}: {
  title: string;
  basicInfo: UnitBasicInfo;
  rentAmount: number;
  homeUniqueId: string;
  // Optional: the caller passes a flag off data that may not have loaded, and this
  // is only ever used as a ternary condition below.
  isGrayscale?: boolean;
}) => {
  return (
    <>
      <LinkBase href={`${BASE_PATHS.HOME}/${homeUniqueId}`}>
        <Text
          variant="h3"
          className={cx(
            'hover:text-dark-navy cursor-pointer',
            isGrayscale && 'text-dark-gray',
          )}
          fontWeight="semibold"
        >
          {title}
        </Text>
      </LinkBase>
      <div
        className={cx('text-p1 font-regular', isGrayscale && 'text-dark-gray')}
      >
        <BulletList
          small
          flexWrap
          listItems={[
            <HomeInfo key="0" unitBasic={basicInfo} />,
            <span key="1">
              {formatMoney(rentAmount)}
              /mo
            </span>,
          ]}
        />
      </div>
    </>
  );
};

type AppCardProps = {
  imageUrl: string;
  topContent?: React.ReactNode;
  bottomContent?: React.ReactNode;
  rounded?: boolean;
  setShowWithdrawModal?: boolean | ((bool: boolean) => void);
  listingApplicationDetails?: ListingApplicationListingApplicationModel;
};

const canWithdrawApplication = (
  listingApplicationDetails?: ListingApplicationListingApplicationModel,
): boolean => {
  if (!listingApplicationDetails) return false;

  const applicationStatus = listingApplicationDetails.application?.status;
  const listingApplicationStatus = listingApplicationDetails.status;

  return (
    applicationStatus === ApplicationStatus.VerificationsCompleted ||
    applicationStatus === ApplicationStatus.Submitted ||
    // @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
    listingApplicationStatus === ListingApplicationStatus.Cancelled
  );
};

export const AppCardTopContent = ({
  imageUrl,
  topContent,
  bottomContent,
  rounded,
  setShowWithdrawModal,
  listingApplicationDetails,
}: AppCardProps) => {
  const canWithdraw = canWithdrawApplication(listingApplicationDetails);

  return (
    <div
      className={cx(styles.container)}
      style={rounded ? { borderRadius: '8px' } : {}}
    >
      <div className="relative flex flex-col md:flex-row">
        <div
          className={cx(styles.homeImage, 'md:w-7xl flex-shrink-0')}
          style={{ backgroundImage: `url(${imageUrl})` }}
        />
        {canWithdraw && (
          <HoverWithdraw
            onWithdraw={() => {
              if (typeof setShowWithdrawModal === 'function') {
                setShowWithdrawModal(true);
              }
            }}
          />
        )}
        <div className={cx(styles.possibleGrid)}>{topContent}</div>
      </div>
      {bottomContent}
    </div>
  );
};
